import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, User, X, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const ShareReportModal = ({ reportId, reportTitle, isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/users/public`);
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        const filtered = res.data
          .filter(u =>
            u.id !== currentUser.id &&
            (u.name?.toLowerCase().includes(query.toLowerCase()) ||
             u.username?.toLowerCase().includes(query.toLowerCase()))
          )
          .slice(0, 8);
        setResults(filtered);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const toggleUser = (user) => {
    setSelectedUsers(prev =>
      prev.some(u => u.id === user.id)
        ? prev.filter(u => u.id !== user.id)
        : [...prev, user]
    );
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Select at least one member to share with");
      return;
    }
    setSharing(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await axios.post(`${API}/dive-reports/${reportId}/share`, {
        recipient_user_ids: selectedUsers.map(u => u.id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      onClose();
      setSelectedUsers([]);
      setQuery("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to share report");
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md z-[10300]" overlayClassName="z-[10299]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-500" />
            Share Dive Report
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            Share "{reportTitle}" with your dive buddies. They'll get a notification and can copy it to their own reports.
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Selected users chips */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map(u => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {u.name || u.username}
                  <button onClick={() => toggleUser(u)} className="hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search members by name or username..."
              className="pl-9"
              data-testid="share-report-search"
            />
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {results.map(user => {
                const isSelected = selectedUsers.some(u => u.id === user.id);
                return (
                  <button
                    key={user.id}
                    onClick={() => toggleUser(user)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                      isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                    data-testid={`share-user-${user.id}`}
                  >
                    {user.profile_picture ? (
                      <img
                        src={getImageUrl(user.profile_picture)}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs font-medium">
                        {(user.name || user.username || "?").charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.name || user.username}</p>
                      {user.username && <p className="text-xs text-gray-500">@{user.username}</p>}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {query.length >= 2 && results.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No members found</p>
          )}

          {/* Share button */}
          <Button
            onClick={handleShare}
            disabled={selectedUsers.length === 0 || sharing}
            className="w-full bg-blue-500 hover:bg-blue-600"
            data-testid="share-report-submit"
          >
            {sharing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sharing...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Share with {selectedUsers.length} member{selectedUsers.length !== 1 ? "s" : ""}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareReportModal;
